import React, { useEffect, useRef } from 'react';
import activityImg from '../../../assets/landing/activity-v2.png';
import SectionShape from '../../../components/SectionShape';

/**
 * Rewards section (#rewards) — "Getting smarter should pay."
 * Mirrors the real KACHNG points structure:
 *   50/receipt · 100 new-merchant bonus · 500/survey · 1,000/referral
 *   500 pts = $1.00, $10 minimum, points don't expire.
 *
 * Stat numbers count up on scroll (self-contained, no GSAP).
 */

// Count-up number, animates from 0 when scrolled into view.
function Count({ staticValue = false, value, prefix = '', suffix = '', decimals = 0, style }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const format = (n) =>
      prefix +
      n.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }) +
      suffix;

    if (prefersReduced || staticValue) {
      el.textContent = format(value);
      return;
    }

    el.textContent = format(0);
    let raf = 0;
    let started = false;

    const run = () => {
      const duration = 1100;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = format(value * eased);
        if (t < 1) raf = requestAnimationFrame(tick);
        else el.textContent = format(value);
      };
      raf = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) {
            started = true;
            run();
            io.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    io.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, [value, prefix, suffix, decimals, staticValue]);

  return (
    <span ref={ref} style={style}>
      {prefix}
      {value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
}

const STATS = [
  { value: 50, unit: 'pts', label: 'points per receipt' },
  { value: 100, unit: 'bonus pts', label: "a merchant you haven't used before" },
  { value: 500, unit: 'pts', label: 'points per survey' },
  { value: 1000, unit: 'pts', label: 'points per referral' },
];

function StatCard({ value, unit, label }) {
  return (
    <div
      data-rv="up"
      style={{
        boxSizing: 'border-box',
        padding: '26px 20px',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: '12px',
        background: 'rgba(255,255,255,0.02)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '8px',
        textAlign: 'center',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '8px' }}>
        <Count
          value={value}
          style={{
            fontSize: 'clamp(36px,3.4vw,48px)',
            fontWeight: 800,
            letterSpacing: '-0.02em',
          }}
        />
        <div style={{ fontSize: 'clamp(15px,1.4vw,19px)', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '-0.01em' }}>
          {unit}
        </div>
      </div>
      <div style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: 1.45 }}>{label}</div>
    </div>
  );
}

export default function Rewards({ mobile = false }) {
  return (
    <section id="rewards" style={{ position: 'relative' }}>
      {/* Flowing ribbon — direct child of <section>, full width; resolves into the ring at Close */}
      {!mobile && <SectionShape kind="ribbon" opacity={0.5} />}

      <div className="mobile-section-inner"
        style={{
          position: 'relative',
          zIndex: 1,
          padding: 'clamp(56px,7vw,88px) clamp(20px,5vw,56px)',
          maxWidth: 'var(--max-width)',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        <h2
          data-rv="up"
          style={{
            margin: '0 0 clamp(40px,5vw,64px)',
            fontSize: 'clamp(38px,4.8vw,68px)',
            fontWeight: 800,
            letterSpacing: '-0.03em',
            lineHeight: 1.02,
            textWrap: 'balance',
            textTransform: 'uppercase',
          }}
        >
          Getting smarter should pay
        </h2>

        {/* Desktop rates and a compact mobile summary share the program data. */}
        {mobile ? <p className="mobile-rewards-summary">Earn {STATS[0].value} points from eligible receipts, plus additional points from new merchants, surveys and referrals.</p> : <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 'clamp(12px,1.4vw,18px)',
            fontVariantNumeric: 'tabular-nums',
            marginBottom: 'clamp(40px,5vw,64px)',
          }}
        >
          {STATS.map((s, i) => (
            <StatCard key={i} {...s} />
          ))}
        </div>}

        {/* Conversion + activity image */}
        <div className="rewards-conversion-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
            gap: 'clamp(40px,5vw,64px)',
            alignItems: 'center',
          }}
        >
          <div data-rv="left">
            <div className="rewards-rate"
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 'clamp(14px,2vw,24px)',
                margin: '0 0 16px',
                fontVariantNumeric: 'tabular-nums',
                flexWrap: 'wrap',
              }}
            >
              <Count staticValue={mobile}
                value={500}
                suffix=" pts"
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'clamp(42px,5.6vw,76px)',
                  fontWeight: 800,
                  letterSpacing: '-0.045em',
                  lineHeight: 1,
                }}
              />
              <span style={{ fontSize: 'clamp(28px,3.5vw,48px)', fontWeight: 200, color: 'var(--text-muted)' }}>=</span>
              <Count staticValue={mobile}
                value={1}
                prefix="$"
                decimals={2}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'clamp(42px,5.6vw,76px)',
                  fontWeight: 800,
                  letterSpacing: '-0.045em',
                  lineHeight: 1,
                  color: 'var(--kachng-green)',
                }}
              />
            </div>
            <p style={{ margin: '0 0 8px', fontSize: '15px', color: 'var(--text-300)' }}>
              Ten dollar minimum. Points don't expire. Rewards and sweepstakes are 18+.
            </p>
            <p style={{ margin: '0 0 24px', fontSize: '15px', color: 'var(--text-300)', fontWeight: 400 }}>
              Each qualifying receipt from an eligible participant is also an entry in the monthly sweepstakes prize drop.
            </p>
            <p className="rewards-comparison" style={{ margin: 0, fontSize: '15px', color: 'var(--text-muted)', borderTop: '1px solid var(--border-hairline)', paddingTop: '16px' }}>
              Most rewards apps hide the conversion rate. Ours is on the homepage.
            </p>
          </div>

          {!mobile && <div
            data-rv="right"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              position: 'relative',
            }}
          >
            <img
              src={activityImg}
              alt="Recent activity — points per receipt"
              style={{
                width: 'min(480px,100%)',
                display: 'block',
                border: '1px solid var(--border-input)',
                borderRadius: '24px',
                boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
                position: 'relative',
              }}
            />
            <div style={{ fontSize: '13px', color: 'var(--text-dim)', fontVariantNumeric: 'tabular-nums' }}>
              Live from the app — every receipt posts within minutes.
            </div>
          </div>}
        </div>
      </div>
    </section>
  );
}